import { Crown, Users, CalendarDays, Megaphone, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useWorkspace} from "@/hooks/useWorkspace";

const ICONS: Record<string, React.ElementType> = {
  Crown, Users, CalendarDays, Megaphone,
};

export default function WorkspaceSwitcher() {
  const { activeConfig, availableWorkspaces, setActiveWorkspace, hasWorkspaces } = useWorkspace();

  if (!hasWorkspaces || availableWorkspaces.length === 0) return null;

  const ActiveIcon = activeConfig ? ICONS[activeConfig.icon] || Crown : Crown;

  // Single workspace — just show badge, no dropdown
  if (availableWorkspaces.length === 1 && activeConfig) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-secondary/80 border border-border">
        <ActiveIcon className={`w-3.5 h-3.5 ${activeConfig.color}`} />
        <span className="text-xs font-medium text-foreground">{activeConfig.shortLabel}</span>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-secondary/80 border border-border hover:bg-secondary transition-colors">
          {activeConfig && (
            <>
              <ActiveIcon className={`w-3.5 h-3.5 ${activeConfig.color}`} />
              <span className="text-xs font-medium text-foreground hidden md:inline">{activeConfig.shortLabel}</span>
            </>
          )}
          <ChevronDown className="w-3 h-3 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <div className="px-3 py-2">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Switch Workspace</p>
        </div>
        <DropdownMenuSeparator />
        {availableWorkspaces.map((ws) => {
          const Icon = ICONS[ws.icon] || Crown;
          const isActive = ws.key === activeConfig?.key;
          return (
            <DropdownMenuItem
              key={ws.key}
              onClick={() => setActiveWorkspace(ws.key)}
              className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer ${isActive ? "bg-primary/5" : ""}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? "bg-primary/10" : "bg-muted"}`}>
                <Icon className={`w-4 h-4 ${ws.color}`} />
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium text-foreground">{ws.label}</div>
                <div className="text-[11px] text-muted-foreground">{ws.description}</div>
              </div>
              {isActive && (
                <Badge variant="secondary" className="text-[9px]">Active</Badge>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
